import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import Swal from 'sweetalert2';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { TranslateService } from '@ngx-translate/core';
import { TwoStepDepositDataService } from './two-step-deposit-data.service';
import { environment } from '@env/environment';
import { svgIconList } from '@shared/svg-icon-list';
import { PortalDepositHttpService } from './portal-deposit-http.service';

@Injectable({
  providedIn: 'root',
})
export class TwoStepDepositService {
  svg: any = svgIconList;
  projectCode = environment.projectCode;

  constructor(
    private portalDepositHttpService: PortalDepositHttpService,
    private loadingBar: LoadingBarService,
    private translateService: TranslateService,
    private router: Router,
    private twoStepDepositDataService: TwoStepDepositDataService,
  ) {}

  /**
   * Cancels a pending deposit callback by its ID.
   * 
   * Note:
   * return value in this func is for leaving page only, don't use return value as indicator of success/failed operation
   *
   * If there is no pending deposit callback, a message is displayed to the user indicating that they have no pending deposit.
   *
   * If a pending deposit callback is found, it is canceled by making a request to the `depositHttpService`. If the cancellation is successful, the step two local storage data is cleared and the page is reloaded.
   *
   * @param stepTwoDeposit - The pending deposit callback to be canceled.
   * @param reload - Whether or not to reload the page after the deposit is canceled.
   */
  async cancelStepTwoDeposit(stepTwoDeposit: any, reload = true) {
    if (!stepTwoDeposit) {
      Swal.fire({
        html:
          '<img class="msg-icon" src="/assets/integrations/popup/red-exclamation.png">' +
          '<div class="text-center m-t-20">' +
          this.translateService.instant('You have no pending deposit.') +
          '</div>',
      });
      return null;
    }

    const result = await Swal.fire({
      customClass: {
        popup: 'customPopup2',
        denyButton: 'deny-button',
        confirmButton: 'confirm-button',
      },
      html: '<div class="alert-svg alert-svg-'+ this.projectCode +'">' + (this.projectCode == 'qp20-dark' ? this.svg.triangleAlertIcon :  this.svg.alertIcon) + '</div>' +
        '<div class="text-center m-t-20">' +
        this.translateService.instant('Are you sure you want to cancel this deposit?') +
        '</div>',
      showDenyButton: true,
      showCloseButton: true,
      showConfirmButton: true,
      reverseButtons: true,
      denyButtonText: this.translateService.instant('NO'),
      confirmButtonText: this.translateService.instant('YES'),
    });

    // For leaving page cancel deposit, default to true (leave) if user choose "Ok".
    // Although this will most likely cause issue if reload=true, but for
    // leaving page reload param should always be false
    if (result.isConfirmed) {
      this.loadingBar.start();

      this.portalDepositHttpService
        .cancelDeposit(stepTwoDeposit.id)
        .subscribe((res) => {
          this.loadingBar.complete();

          if (res.success) {
            if (reload) {
              this.twoStepDepositDataService.clearStepTwoLocalStorageAndReload();
            } else {
              this.twoStepDepositDataService.clearStepTwoLocalStorage();
            }
          }
        });

      return false;
    }

    return false;
  }

  /**
   * Indicate that a deposit is paid (but backend side still waiting for vendor callback)
   *
   * This function is used to clean up the local storage and redirect the user to the member history page after a successful two-step deposit.
   */
  stepTwoDepositPaid() {
    this.twoStepDepositDataService.clearStepTwoLocalStorage();

    const tab = 1;
    return this.router.navigate(['/member/history', { tab }]);
  }

  /**
   * Updates the step two deposit with the provided data.
   *
   * If there is no pending deposit, a message is displayed to the user indicating that they have no pending deposit.
   *
   * If a pending deposit is found, the step two deposit is updated by making a request to the `portalDepositHttpService`. The updated deposit data is returned.
   *
   * @param stepTwoDeposit - The pending deposit to be updated.
   * @param data - The data to update the deposit with.
   * @returns The updated deposit data.
   */
  stepTwoUpdateDeposit(stepTwoDeposit: any , data: any) {
    if (!stepTwoDeposit) {
      Swal.fire({
        html:
          '<img class="msg-icon" src="/assets/integrations/popup/red-exclamation.png">' +
          '<div class="text-center m-t-20">' +
            this.translateService.instant('You have no pending deposit.') +
          "</div>",
      });
      return null;
    }

    return this.portalDepositHttpService.stepTwoUpdateDeposit(
      stepTwoDeposit.id,
      data
    );
  }

  /**
   * Displays a message to the user indicating that their deposit session has expired,
   * and clears the step two local storage data and reloads the page.
   */
  showDepositExpiredMsg(stepTwoDeposit: any) {
    Swal.fire({
      html:
        '<img class="msg-icon" src="/assets/images/message-icon/expired.svg">' +
        '<h3 style="text-transform: uppercase; font-size: 1.3rem; font-weight: bold;">' + this.translateService.instant('Payment Timeout') + '</h3>' +
        '<div class="text-center m-t-20">' +
          this.translateService.instant(this.twoStepDepositDataService.getSessionExpiredMsg()) +
        '</div>',
      showCloseButton: true,
    }).then(() => {
      this.loadingBar.start();

      this.portalDepositHttpService.changeDepositToExpired(stepTwoDeposit.id).subscribe((res) => {
        this.loadingBar.complete();

        if (res.success) {
          this.twoStepDepositDataService.clearStepTwoLocalStorageAndReload();
        }
      });
    });
  }

  /**
   * Return false = stay on page
   * Return true = leave page
   * 
   * Handles the user leaving the two-step deposit page, 
   * providing options to continue the payment, cancel the deposit, or stay on the page.
   *
   * @param lsStepTwoDeposit - A string representation of the pending callback deposit object, stored in local storage.
   * @returns `false` if the user chooses to continue the payment or the function is unable to determine the user's choice, `true` if the user chooses to cancel the deposit.
   */
  async handleLeavingStepTwoPage(lsStepTwoDeposit: string) {
    // this.loadingBar.start();
    this.loadingBar.complete();
    const result = await this.showLeavingPageAlert();

    // continue payment
    if (result.isConfirmed || result.isDismissed) {
      return false;
    }

    // cancel deposit
    if (result.isDenied) {
      const stepTwoDeposit = JSON.parse(lsStepTwoDeposit);

      const res = await this.cancelStepTwoDeposit(stepTwoDeposit, true);
      return res;
    }

    // just in case unable to get isConfirmed, isDismissed and isDenied, we let user to stay, but this is very unlikely
    return false;
  }

  /**
   * Displays a confirmation dialog to the user when they attempt to leave the current page,
   * warning them that doing so will discard their payment progress.
   *
   * The dialog provides options to either cancel the deposit or continue with the payment.
   *
   * @returns A Promise that resolves to the user's choice (either 'confirm' or 'deny') when the dialog is closed.
   */
  showLeavingPageAlert() {
    return Swal.fire({
      html:
        '<div class="alert-svg">' + this.svg.triangleAlertIcon + '</div>' +
        '<div class="text-center mt-2 mb-3 px-5">' +
          this.translateService.instant(this.twoStepDepositDataService.getLeavingPageMsg()) +
        '</div>',
      buttonsStyling: false,
      showCloseButton: true,
      showDenyButton: true,
      showConfirmButton: true,
      reverseButtons: true,
      denyButtonText: this.translateService.instant('Cancel Deposit'),
      confirmButtonText: this.translateService.instant('Continue Payment'),
      padding: '50px 0 3rem',
      customClass: {
        denyButton: 'swal-cancel-deposit-btn',
        confirmButton: 'swal-continue-payment-btn',
      },
    });
  }
}